import React, { useState, useMemo, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Area,
  AreaChart
} from 'recharts';
import { TrendingUp, TrendingDown, Info, AlertCircle, RefreshCw } from 'lucide-react';
import { Card, CardContent, CardHeader } from '../ui/card';
import { Button } from '../ui/button';
import { fetchHistoricalData } from '../../services/api';
import AnimatedCounter from '../ui/AnimatedCounter';
import { useLanguage } from '../../context/LanguageContext';

const timeRanges = [
  { label: '1D', days: 1 },
  { label: '1W', days: 7 },
  { label: '1M', days: 30 },
  { label: '3M', days: 90 },
  { label: '6M', days: 180 },
  { label: '1Y', days: 365 },
  { label: '3Y', days: 1095 },
  { label: '5Y', days: 1825 },
];

// Custom Tooltip component moved outside
const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    return (
      <motion.div 
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        className="bg-[#12121a] border border-[#1e1e2e] rounded-lg p-3 shadow-xl"
      >
        <p className="text-xs text-gray-400 mb-1">
          {new Date(label).toLocaleDateString('en-US', {
            month: 'long',
            day: 'numeric',
            year: 'numeric',
          })}
        </p>
        <p className="text-sm font-semibold text-[#D4AF37]">
          €{payload[0].value.toFixed(2)} / gram
        </p>
      </motion.div>
    );
  }
  return null;
};

const PerformanceChart = () => {
  const { t } = useLanguage();
  const [selectedRange, setSelectedRange] = useState(1825);
  const [historicalData, setHistoricalData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dataInfo, setDataInfo] = useState(null);

  useEffect(() => {
    const loadData = async () => {
      try {
        setIsLoading(true);
        setError(null);
        const result = await fetchHistoricalData(5);
        setHistoricalData(result.data || []);
        setDataInfo({
          source: result.source,
          isSimulated: result.isSimulated,
          lastUpdated: result.lastUpdated,
          note: result.note
        });
      } catch (err) {
        console.error('Failed to load historical data:', err);
        setError('Unable to load historical data');
        setHistoricalData([]);
      } finally {
        setIsLoading(false);
      }
    };
    
    loadData();
  }, []);

  const filteredData = useMemo(() => {
    if (!historicalData || historicalData.length === 0) return [];
    
    const cutoffDate = new Date();
    cutoffDate.setDate(cutoffDate.getDate() - selectedRange);
    const cutoffStr = cutoffDate.toISOString().split('T')[0];
    
    const filtered = historicalData.filter(item => item.date >= cutoffStr);
    
    let sampleRate = 1;
    if (selectedRange > 365) {
      sampleRate = Math.max(1, Math.floor(filtered.length / 100));
    } else if (selectedRange > 90) {
      sampleRate = Math.max(1, Math.floor(filtered.length / 60));
    }
    
    return filtered.filter((_, index, arr) => {
      return index % sampleRate === 0 || index === arr.length - 1;
    });
  }, [historicalData, selectedRange]);

  const stats = useMemo(() => {
    if (filteredData.length < 2) return null;
    
    const startPrice = filteredData[0].price;
    const endPrice = filteredData[filteredData.length - 1].price;
    const change = endPrice - startPrice;
    const percentChange = ((change / startPrice) * 100).toFixed(2);
    
    return {
      startPrice: startPrice.toFixed(2),
      endPrice: endPrice.toFixed(2),
      change: change.toFixed(2),
      percentChange,
      isPositive: change >= 0,
    };
  }, [filteredData]);

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    if (selectedRange <= 30) {
      return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    }
    return date.toLocaleDateString('en-US', { month: 'short', year: '2-digit' });
  };

  if (error && historicalData.length === 0) {
    return (
      <section id="performance" className="py-16 md:py-24 bg-[#0a0a0f]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Card className="bg-yellow-500/10 border-yellow-500/30">
            <CardContent className="p-8 text-center">
              <AlertCircle className="w-12 h-12 text-yellow-500 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-white mb-2">{t('dataTemporarilyUnavailable')}</h3>
              <p className="text-gray-400">{t('historicalDataUnavailable')}</p>
            </CardContent>
          </Card>
        </div>
      </section>
    );
  }

  return (
    <section id="performance" className="py-16 md:py-24 bg-[#0a0a0f]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <motion.div 
          className="text-center mb-8 md:mb-10"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
        >
          <span className="inline-block text-sm font-semibold text-[#D4AF37] uppercase tracking-wider mb-3">
            {t('historicalPerformance')}
          </span>
          <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-4">
            {t('goldPriceEUR').split(' ')[0]} {t('goldPriceEUR').split(' ')[1]} <span className="text-gradient-gold">{t('goldPriceEUR').split(' ').slice(2).join(' ') || '(EUR)'}</span>
          </h2>
          <p className="text-base md:text-lg text-gray-400 max-w-2xl mx-auto px-4">
            {t('trackGoldPerformance')}
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.2 }}
        >
          <Card className="bg-[#12121a] border-[#1e1e2e] rounded-2xl overflow-hidden">
            <CardHeader className="pb-0 border-b border-[#1e1e2e]">
              <div className="flex flex-col gap-4 p-4">
                {/* Range Selector - Scrollable on mobile */}
                <div className="flex items-center gap-1 p-1 bg-[#0a0a0f] rounded-xl overflow-x-auto scrollbar-hide -mx-1 px-1">
                  {timeRanges.map((range, index) => (
                    <motion.div
                      key={range.label}
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                    >
                      <Button
                        onClick={() => setSelectedRange(range.days)}
                        size="sm"
                        className={`px-3 py-2 text-xs sm:text-sm font-medium rounded-lg transition-all whitespace-nowrap min-w-[40px] ${
                          selectedRange === range.days
                            ? 'btn-gold text-black'
                            : 'bg-transparent text-gray-400 hover:text-white hover:bg-[#1e1e2e]'
                        }`}
                      >
                        {range.label}
                      </Button>
                    </motion.div>
                  ))}
                </div>

                {/* Stats - Responsive grid */}
                <AnimatePresence mode="wait">
                  {stats && !isLoading && (
                    <motion.div 
                      key={selectedRange}
                      initial={{ opacity: 0, y: -10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: 10 }}
                      transition={{ duration: 0.3 }}
                      className="grid grid-cols-3 gap-2 sm:gap-4 md:gap-6"
                    >
                      <div className="text-center sm:text-right">
                        <p className="text-[10px] sm:text-xs text-gray-500 uppercase tracking-wide">{t('start')}</p>
                        <p className="text-base sm:text-lg font-semibold text-white">
                          €<AnimatedCounter value={stats.startPrice} decimals={2} duration={0.8} />
                        </p>
                      </div>
                      <div className="text-center sm:text-right">
                        <p className="text-[10px] sm:text-xs text-gray-500 uppercase tracking-wide">{t('current')}</p>
                        <p className="text-base sm:text-lg font-semibold text-[#D4AF37]">
                          €<AnimatedCounter value={stats.endPrice} decimals={2} duration={0.8} />
                        </p>
                      </div>
                      <div className="text-center sm:text-right">
                        <p className="text-[10px] sm:text-xs text-gray-500 uppercase tracking-wide">{t('change')}</p>
                        <motion.div 
                          className={`flex items-center justify-center sm:justify-end gap-1 ${
                            stats.isPositive ? 'text-emerald-400' : 'text-red-400'
                          }`}
                          initial={{ scale: 0.8 }}
                          animate={{ scale: 1 }}
                          transition={{ type: "spring", stiffness: 200 }}
                        >
                          {stats.isPositive ? (
                            <TrendingUp className="w-3 h-3 sm:w-4 sm:h-4" />
                          ) : (
                            <TrendingDown className="w-3 h-3 sm:w-4 sm:h-4" />
                          )}
                          <span className="text-base sm:text-lg font-semibold">
                            {stats.isPositive ? '+' : ''}{stats.percentChange}%
                          </span>
                        </motion.div>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </CardHeader>

            <CardContent className="p-2 sm:p-4 md:p-6">
              {/* Chart */}
              <div className="h-[250px] sm:h-[300px] md:h-[400px] w-full">
                {isLoading ? (
                  <div className="h-full flex items-center justify-center">
                    <motion.div 
                      className="flex items-center gap-2 text-gray-400"
                      animate={{ opacity: [0.5, 1, 0.5] }}
                      transition={{ duration: 1.5, repeat: Infinity }}
                    >
                      <RefreshCw className="w-5 h-5 animate-spin text-[#D4AF37]" />
                      <span>{t('loadingChartData')}</span>
                    </motion.div>
                  </div>
                ) : (
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={filteredData} margin={{ top: 10, right: 10, left: -10, bottom: 0 }}>
                      <defs>
                        <linearGradient id="goldGradient" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#D4AF37" stopOpacity={0.4} />
                          <stop offset="95%" stopColor="#D4AF37" stopOpacity={0} />
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" stroke="#1e1e2e" vertical={false} />
                      <XAxis
                        dataKey="date"
                        tickFormatter={formatDate}
                        tick={{ fontSize: 10, fill: '#6b7280' }}
                        axisLine={{ stroke: '#1e1e2e' }}
                        tickLine={false}
                        interval="preserveStartEnd"
                        minTickGap={30}
                      />
                      <YAxis
                        tick={{ fontSize: 10, fill: '#6b7280' }}
                        axisLine={false}
                        tickLine={false}
                        tickFormatter={(value) => `€${value}`}
                        domain={['auto', 'auto']}
                        width={50}
                      />
                      <Tooltip content={<CustomTooltip />} />
                      <Area
                        type="monotone"
                        dataKey="price"
                        stroke="#D4AF37"
                        strokeWidth={2}
                        fill="url(#goldGradient)"
                        animationDuration={1500}
                        animationEasing="ease-out"
                      />
                    </AreaChart>
                  </ResponsiveContainer>
                )}
              </div>

              {/* Source Note */}
              <motion.div 
                className="flex items-start gap-2 mt-4 md:mt-6 p-3 md:p-4 bg-[#0a0a0f] rounded-xl border border-[#1e1e2e]"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.5 }}
              >
                <Info className="w-4 h-4 text-gray-500 mt-0.5 flex-shrink-0" />
                <div className="text-[10px] sm:text-xs text-gray-500">
                  <span className="font-medium text-gray-400">{t('source')}:</span> {dataInfo?.source || 'LBMA'}. 
                  {dataInfo?.isSimulated && (
                    <span className="ml-1 text-yellow-500">({t('indicativeData')})</span>
                  )}
                  <span className="ml-1 hidden sm:inline">
                    {t('dataDelayedInfo')}
                  </span>
                </div>
              </motion.div>
            </CardContent>
          </Card>
        </motion.div>
      </div>
    </section>
  );
};

export default PerformanceChart;
